import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FaBullhorn, FaClock, FaUserCheck } from "react-icons/fa";
import Header from "../../../components/IntranetHeader";
import Footer from "../../../components/Footer";
import { supabase } from "../../../supabaseClient";

function formatTime(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

function waitMinutes(value, now) {
  const start = new Date(value).getTime();
  if (!Number.isFinite(start)) return 0;
  return Math.max(0, Math.floor((now - start) / 60000));
}

export default function CheckInQueue({ session, profile }) {
  const [appointments, setAppointments] = useState([]);
  const [destinations, setDestinations] = useState({});
  const [loading, setLoading] = useState(true);
  const [workingId, setWorkingId] = useState(null);
  const [error, setError] = useState("");
  const [now, setNow] = useState(Date.now());

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const from = new Date();
    from.setHours(0, 0, 0, 0);
    const to = new Date(from);
    to.setDate(to.getDate() + 1);

    const { data, error: loadError } = await supabase
      .from("appointments")
      .select(`
        id,
        portal_account_id,
        patient_id,
        patient_full_name,
        roleplay_name,
        department,
        appointment_datetime,
        location,
        checked_in_at,
        queue_status,
        called_at,
        called_location,
        appointment_status,
        status,
        clinician:profiles!appointments_clinician_fkey(id, full_name)
      `)
      .not("checked_in_at", "is", null)
      .gte("checked_in_at", from.toISOString())
      .lt("checked_in_at", to.toISOString())
      .neq("status", false)
      .order("checked_in_at", { ascending: true });

    if (loadError) {
      console.error(loadError);
      // Some installations do not expose the relationship alias. Retry without it.
      const fallback = await supabase
        .from("appointments")
        .select("id, portal_account_id, patient_id, patient_full_name, roleplay_name, department, clinician, appointment_datetime, location, checked_in_at, queue_status, called_at, called_location, appointment_status, status")
        .not("checked_in_at", "is", null)
        .gte("checked_in_at", from.toISOString())
        .lt("checked_in_at", to.toISOString())
        .neq("status", false)
        .order("checked_in_at", { ascending: true });

      if (fallback.error) {
        setError("The check-in queue could not be loaded. Run ADD_CLINICAL_WORKFLOWS.sql first.");
        setAppointments([]);
      } else {
        setAppointments(fallback.data || []);
      }
    } else {
      setAppointments(data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30000);
    return () => window.clearInterval(timer);
  }, []);

  async function callPatient(appointment) {
    const destination = String(destinations[appointment.id] || "Consultation Room 1").trim();
    if (!destination) return;
    setWorkingId(appointment.id);

    const calledAt = new Date().toISOString();
    const { error: updateError } = await supabase
      .from("appointments")
      .update({
        queue_status: "Called",
        called_at: calledAt,
        called_location: destination,
        appointment_status: "Checked In",
      })
      .eq("id", appointment.id);

    if (updateError) {
      setError(updateError.message || "The patient could not be called.");
      setWorkingId(null);
      return;
    }

    if (appointment.portal_account_id) {
      await supabase.from("patient_messages").insert({
        portal_account_id: appointment.portal_account_id,
        patient_id: appointment.patient_id || null,
        subject: "Please proceed to your appointment",
        category: "Appointment",
        body: `You have been called. Please proceed to ${destination}.`,
        action_url: "/patient-portal",
        created_by: session?.user?.id || null,
      });
    }

    await load();
    setWorkingId(null);
  }

  async function markSeen(appointment) {
    setWorkingId(appointment.id);
    const { error: updateError } = await supabase
      .from("appointments")
      .update({ queue_status: "Seen" })
      .eq("id", appointment.id);

    if (updateError) setError(updateError.message || "The queue status could not be updated.");
    else await load();
    setWorkingId(null);
  }

  const visible = useMemo(
    () => appointments.filter((appointment) => String(appointment.queue_status || "Waiting") !== "Seen"),
    [appointments],
  );

  return (
    <div className="flex flex-col min-h-screen">
      <Header session={session} profile={profile} />
      <main className="nhsuk-width-container pguh-main">
        <div className="pguh-section-heading">
          <div>
            <span className="pguh-eyebrow">eCAMIS live queue</span>
            <h1 className="nhsuk-heading-l">Patient check-in queue</h1>
            <p className="nhsuk-body-m">Patients who checked in online today appear here until marked as seen.</p>
          </div>
          <button className="nhsuk-button nhsuk-button--secondary" type="button" onClick={load} disabled={loading}>{loading ? "Refreshing…" : "Refresh"}</button>
        </div>

        {error && <div className="pguh-timesheet-message pguh-timesheet-message--error">{error}</div>}

        <section className="pguh-checkin-summary">
          <div><span>Waiting / called</span><strong>{visible.length}</strong></div>
          <div><span>Seen today</span><strong>{appointments.filter((a) => a.queue_status === "Seen").length}</strong></div>
        </section>

        <div className="pguh-checkin-queue">
          {visible.length === 0 ? (
            <div className="pguh-empty-state"><FaUserCheck aria-hidden="true" /><h3>No patients waiting</h3><p>Online check-ins will appear here automatically.</p></div>
          ) : visible.map((appointment) => {
            const queueStatus = appointment.queue_status || "Waiting";
            return (
              <article key={appointment.id} className={`pguh-checkin-card pguh-checkin-card--${queueStatus.toLowerCase()}`}>
                <div className="pguh-checkin-card__identity">
                  <span>{queueStatus}</span>
                  <h2>{appointment.patient_full_name || appointment.roleplay_name || "Patient"}</h2>
                  <p>{appointment.department || "Appointment"} · {appointment.location || "Hospital"}</p>
                  <div className="pguh-checkin-card__time">
                    <FaClock aria-hidden="true" /> Checked in {formatTime(appointment.checked_in_at)} · waiting {waitMinutes(appointment.checked_in_at, now)} min
                  </div>
                  {appointment.called_location && <strong>Called to: {appointment.called_location}</strong>}
                  <Link to={appointment.patient_id ? `/intranet/ecamis/patient/${appointment.patient_id}` : `/intranet/ecamis/appointment/${appointment.id}`}>Open record</Link>
                </div>

                <div className="pguh-checkin-card__actions">
                  <label className="nhsuk-label" htmlFor={`destination-${appointment.id}`}>Call to</label>
                  <input
                    id={`destination-${appointment.id}`}
                    className="nhsuk-input"
                    value={destinations[appointment.id] ?? appointment.called_location ?? "Consultation Room 1"}
                    onChange={(event) => setDestinations((current) => ({ ...current, [appointment.id]: event.target.value }))}
                  />
                  <button className="nhsuk-button" type="button" onClick={() => callPatient(appointment)} disabled={workingId === appointment.id}>
                    <FaBullhorn aria-hidden="true" /> {queueStatus === "Called" ? "Call again" : "Call patient"}
                  </button>
                  <button className="nhsuk-button nhsuk-button--secondary" type="button" onClick={() => markSeen(appointment)} disabled={workingId === appointment.id}>Mark seen</button>
                </div>
              </article>
            );
          })}
        </div>
      </main>
      <Footer />
    </div>
  );
}
